import {h, Component} from 'preact'
import Button from '../ui-components/Button'
import TextButton from '../ui-components/TextButton'
import Icon from '../assets'
import {HomepageElement} from '../homepage'
import {arrayMove} from 'react-sortable-hoc'
import Axios from 'axios'
import {isHtmlElement} from '../../libs/preact-entry'
import HomepageEditor from './editor'
import OrderAnimator from './orderAnimator'
import Portal from 'preact-portal'
import NewWidgetButton, {TypesThatCanBeAdded} from './new-button'

interface Props {
  elements: Array<HomepageElement>
}

export type ElementChange = Pick<HomepageElement, 'type' | 'config' | 'title'>

export type DonePayload = {idx: number, element: ElementChange}
export type MovePayload = {direction: 'up' | 'down'}
export type VisiblePayload = boolean

interface State {
  elements: Array<HomepageElement>,
  expanded: boolean,
  editingId: number | null
}

export default class CustomizationToolbar extends Component<Props, State> {
  constructor(props: Props) {
    super()

    this.state = {
      elements: props.elements.map((el, idx) => Object.assign(
        {},
        el,
        {id: idx}
      )),
      editingId: null,
      expanded: true
    }
    this.onDone = this.onDone.bind(this)
    this.onRemove = this.onRemove.bind(this)
  }

  onMove(positionToMoveUp: number, {direction}: MovePayload) {
    if (direction === 'down') {
      positionToMoveUp += 1
    }
    this.setState({
      elements: arrayMove(this.state.elements, positionToMoveUp, positionToMoveUp - 1)
    })
  }

  onAddWidget(type: TypesThatCanBeAdded) {
    const newWidgetButton = document.querySelector('.widget-editor-new-button')

    const newId = this.state.elements.length
    const newElement = {
      type: type,
      config: {
        html: ''
      },
      id: newId,
      active: false
    }
    const footer = this.state.elements.pop()
    if (!footer) throw new Error('There should be some elements in the list')

    const elements = this.state.elements.concat([newElement, footer])
    if (!newWidgetButton) return
    const container = document.createElement('div')
    container.classList.add(
      'homepage-widget-container',
      `homepage-widget-container--${type}`,
      `homepage-widget-container--index-${newId}`,
      'homepage-widget-container--inactive'
    )

    const placeholder = document.createElement('div')
    placeholder.classList.add('homepage-widget-placeholder')
    container.appendChild(placeholder)

    newWidgetButton.parentElement?.insertBefore(container, newWidgetButton)

    this.setState({
      elements,
      editingId: newId
    })
  }

  onRemove(removedIndex: number) {
    const elements = this.state.elements.filter((_, index) => removedIndex !== index)
    this.setState({elements: elements})
  }

  onDone(change: DonePayload) {
    this.setState({
      editingId: null,
      elements: this.state.elements.map((el, index) => {
        if (index !== change.idx) return el
        if (change.element.type !== el.type) return el
        return Object.assign({}, el, change.element)
      })
    })
  }

  onVisibleToggle(idx: number, visible: boolean) {
    const elements = this.state.elements.map((el, index) => {
      if (idx !== index) return el
      return {
        ...el,
        active: visible
      }
    })
    this.setState({elements})
  }

  render() {
    return <div class='translation--switch customization-toolbar'>
      <div class={`customization-toolbar__expanded-controls ${this.state.expanded ? 'customization-toolbar__expanded-controls--expanded' : ''}`}>
        <span class='customization-toolbar__dot' />

        <span style={{marginRight: '16px'}}>Customization Mode</span>

        <Button insided small style={{marginRight: '8px'}} onClick={async () => {
          await Axios.post('/branding/updateWidgets', {
            elements: this.state.elements
          })
          window.location.href = '/'
        }} qaClasses='qa-save-customization'>Save changes</Button>
        <Button small insidedSecondary style={{marginRight: '8px'}} onClick={() => window.location.href = '/'}>Cancel</Button>
      </div>

      <TextButton style={{
        display: 'flex',
        alignItems: 'center',
        paddingRight: '8px',
        paddingLeft: '8px'
      }} onClick={() => this.setState({expanded: !this.state.expanded})}>
        <Icon size={14} color='#6B707E' icon={this.state.expanded ? 'chevronRight' : 'chevronLeft'} />
      </TextButton>

      {
        this.state.elements.map((el, idx, {length}) => {
          const className = `.homepage-widget-container--index-${el.id}`
          const widgetContainer = document.querySelector(className)
          if (!isHtmlElement(widgetContainer)) {
            throw Error(`Expected "${className}" to be in the DOM but it was not found`)
          }

          const isMovable = ['footer', 'header', 'navigation_bar'].indexOf(el.type) < 0

          return <OrderAnimator isMovable={isMovable} idx={idx} id={el.id} key={el.id} into={widgetContainer}>
            <HomepageEditor
              idx={idx}
              edit={el.id === this.state.editingId}
              isMovable={isMovable}
              homepageElement={el}
              totalCount={length}
              parentEl={widgetContainer}
              onEdit={() => this.setState({editingId: el.id})}
              onDone={this.onDone}
              onClose={() => this.setState({editingId: null})}
              onMove={dir => this.onMove(idx, dir)}
              onVisibleToggle={(visible: VisiblePayload) => this.onVisibleToggle(idx, visible)}
              onRemove={() => this.onRemove(idx)}
            />
          </OrderAnimator>
        })
      }

      <Portal into='.widget-editor-new-button'>
        <NewWidgetButton onClick={(type: TypesThatCanBeAdded) => this.onAddWidget(type)} />
      </Portal>

    </div>
  }
}
